import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Star, Wallet, Gift, Handshake, Calendar, AlertCircle, Tag, Megaphone, Sparkles } from 'lucide-react';
import api from '../services/api';

const fmtData = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};

const diasRestantes = (iso) => {
  if (!iso) return null;
  try {
    const fim = new Date(iso); const agora = new Date();
    const ms = fim - agora;
    if (ms < 0) return -1;
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

const TerminaBadge = ({ dataFim }) => {
  const dias = diasRestantes(dataFim);
  if (dias === null) return null;
  if (dias < 0) return <Badge variant="outline" className="text-xs bg-gray-100 text-gray-500">Expirou</Badge>;
  if (dias === 0) return <Badge className="text-xs bg-red-100 text-red-700 border-red-300">Termina hoje</Badge>;
  if (dias <= 7) return <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300">Termina em {dias}d</Badge>;
  if (dias <= 30) return <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-200">{dias} dias</Badge>;
  return <Badge variant="outline" className="text-xs">Activa</Badge>;
};

const CampanhaCard = ({ icon: Icon, cor, nome, descricao, dataInicio, dataFim, regras, badges = [], testid }) => (
  <div className="border rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
    <div className="flex items-start gap-3">
      <div className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${cor}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <h3 className="font-semibold text-sm">{nome}</h3>
          <div className="flex gap-1.5 flex-wrap">
            {badges.map((b, i) => <Badge key={i} variant="outline" className="text-xs">{b}</Badge>)}
            <TerminaBadge dataFim={dataFim} />
          </div>
        </div>
        {descricao && <p className="text-xs text-muted-foreground mt-1">{descricao}</p>}
        {(dataInicio || dataFim) && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1.5">
            <Calendar className="h-3 w-3" />
            {fmtData(dataInicio) || '?'} → {fmtData(dataFim) || 'sem fim'}
          </div>
        )}
        {regras && regras.length > 0 && (
          <ul className="mt-2 space-y-1">
            {regras.map((r, i) => (
              <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                <span className="text-primary mt-0.5">•</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </div>
);

const SeccaoBlock = ({ titulo, icon: Icon, count, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card data-testid={`bloco-${titulo.toLowerCase()}`}>
      <CardHeader
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5 text-primary" />
          {titulo}
          <Badge variant="outline" className="ml-1">{count}</Badge>
          <span className="ml-auto text-xs text-muted-foreground">{open ? '▾' : '▸'}</span>
        </CardTitle>
      </CardHeader>
      {open && <CardContent>{count === 0
        ? <p className="text-sm text-muted-foreground italic">Nenhuma activa de momento.</p>
        : <div className="space-y-3">{children}</div>}</CardContent>}
    </Card>
  );
};

const CampanhasEmVigorTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/colaborador/campanhas-em-vigor')
      .then(r => setData(r.data))
      .catch(e => setErro(e.response?.data?.detail || 'Erro a carregar'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (erro) return (
    <div className="flex flex-col items-center py-12 text-center" data-testid="erro">
      <AlertCircle className="h-12 w-12 text-red-400 mb-2" />
      <p className="text-sm text-red-600">{erro}</p>
    </div>
  );
  if (!data) return null;

  const cashbackF = data.cashback;
  const fidelizacaoF = data.fidelizacao;
  const parceriasF = data.parcerias;
  const promocoesF = data.promocoes;
  const pontosCartao = data.pontos_cartao;
  const ciclo = data.ciclo;

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="campanhas-em-vigor">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            Campanhas em Vigor
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Tudo o que está activo agora — para informar os clientes correctamente.
          </p>
        </div>
      </div>

      {/* Nota: apenas visualização */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2.5" data-testid="nota-visualizacao">
        <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-800">
          Este painel é apenas <strong>visualização</strong> do que está activo agora.
          Para criar/editar campanhas usa os menus respectivos: <em>Fidelização → Campanhas & Comunicação</em>, <em>Cashback</em> e <em>Parcerias</em>.
        </p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        {[
          { label: 'Ciclo Fid.', count: data.totais.ciclo, cor: 'bg-indigo-50 text-indigo-700' },
          { label: 'Pontos Cartão', count: data.totais.pontos_cartao, cor: 'bg-amber-50 text-amber-700' },
          { label: 'Cashback', count: data.totais.cashback, cor: 'bg-emerald-50 text-emerald-700' },
          { label: 'Fidelização', count: data.totais.fidelizacao, cor: 'bg-blue-50 text-blue-700' },
          { label: 'Parcerias', count: data.totais.parcerias, cor: 'bg-purple-50 text-purple-700' },
          { label: 'Promoções', count: data.totais.promocoes, cor: 'bg-rose-50 text-rose-700' },
        ].map((s, i) => (
          <div key={i} className={`rounded-lg p-3 text-center ${s.cor}`}>
            <p className="text-xs font-medium uppercase tracking-wide">{s.label}</p>
            <p className="text-2xl font-bold mt-0.5">{s.count}</p>
          </div>
        ))}
      </div>

      {/* Ciclo de Fidelização — bónus de adesão e bloqueios */}
      {ciclo && ciclo.ativo && (
        <SeccaoBlock titulo="Ciclo de Fidelização" icon={Sparkles} count={1}>
          <div className="border rounded-xl p-4 bg-white shadow-sm" data-testid="ciclo-fidelizacao">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-700">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <h3 className="font-semibold text-sm">Bónus de Adesão</h3>
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                    {Number(ciclo.bonus_valor || 5).toFixed(0)}€ · {ciclo.bonus_validade_dias}d
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Como funciona o ciclo do novo cliente</p>
                <ul className="mt-2 space-y-1">
                  {(ciclo.regras || []).map((r, i) => (
                    <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </SeccaoBlock>
      )}

      {/* Pontos do Cartão de Fidelização (Cliente) */}
      {pontosCartao && pontosCartao.ativo && (
        <SeccaoBlock titulo="Pontos do Cartão" icon={Star} count={1}>
          <div className="border rounded-xl p-4 bg-white shadow-sm" data-testid="pontos-cartao">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center bg-amber-50 text-amber-700">
                <Star className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm">Como o cliente acumula pontos</h3>
                <ul className="mt-2 space-y-1">
                  {pontosCartao.regras.map((r, i) => (
                    <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </SeccaoBlock>
      )}

      {/* Cashback */}
      <SeccaoBlock titulo="Cashback" icon={Wallet} count={cashbackF.length}>
        {cashbackF.map(c => (
          <div key={c.id} className="border rounded-xl p-4 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid={`cashback-${c.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-700">
                <Wallet className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <h3 className="font-semibold text-sm">{c.nome}</h3>
                  <div className="flex gap-1.5 flex-wrap">
                    {c.em_acumulacao && <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Acumulação activa</Badge>}
                    {c.em_resgate && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">Período de resgate</Badge>}
                    <TerminaBadge dataFim={c.data_fim} />
                  </div>
                </div>

                {/* Janelas explícitas */}
                <div className="mt-2 space-y-1">
                  {c.data_inicio_acumulacao_fmt && c.data_fim_acumulacao_fmt && (
                    <div className="text-xs flex items-start gap-1.5 bg-emerald-50/50 rounded px-2 py-1 border border-emerald-100">
                      <span className="text-emerald-700 font-semibold">📥 Ganha:</span>
                      <span className="text-slate-700">de {c.data_inicio_acumulacao_fmt} a {c.data_fim_acumulacao_fmt}</span>
                    </div>
                  )}
                  {c.data_inicio_resgate_fmt && c.data_fim_resgate_fmt && (
                    <div className="text-xs flex items-start gap-1.5 bg-purple-50/50 rounded px-2 py-1 border border-purple-100">
                      <span className="text-purple-700 font-semibold">📤 Levanta:</span>
                      <span className="text-slate-700">de {c.data_inicio_resgate_fmt} a {c.data_fim_resgate_fmt}</span>
                    </div>
                  )}
                </div>

                {c.regras && c.regras.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {c.regras.map((r, i) => (
                      <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ))}
      </SeccaoBlock>

      {/* Fidelização */}
      <SeccaoBlock titulo="Fidelização" icon={Gift} count={fidelizacaoF.length}>
        {fidelizacaoF.map(c => (
          <CampanhaCard
            key={c.id}
            testid={`fidelizacao-${c.id}`}
            icon={Gift}
            cor="bg-blue-50 text-blue-700"
            nome={c.nome}
            descricao={c.descricao}
            dataInicio={c.data_inicio}
            dataFim={c.data_fim}
            regras={c.regras}
          />
        ))}
      </SeccaoBlock>

      {/* Parcerias */}
      <SeccaoBlock titulo="Parcerias" icon={Handshake} count={parceriasF.length}>
        {parceriasF.map(p => (
          <CampanhaCard
            key={p.id}
            testid={`parceria-${p.id}`}
            icon={Handshake}
            cor="bg-purple-50 text-purple-700"
            nome={p.nome}
            descricao={p.objetivo}
            dataInicio={p.data_inicio}
            dataFim={p.data_fim}
            regras={p.regras}
            badges={[p.tipo_entidade, p.modelo].filter(Boolean)}
          />
        ))}
      </SeccaoBlock>

      {/* Promoções */}
      {data.totais.promocoes > 0 && (
        <SeccaoBlock titulo="Promoções" icon={Tag} count={promocoesF.length}>
          {promocoesF.map(c => (
            <CampanhaCard
              key={c.id}
              testid={`promocao-${c.id}`}
              icon={Tag}
              cor="bg-rose-50 text-rose-700"
              nome={c.nome}
              descricao={c.descricao}
              dataInicio={c.data_inicio}
              dataFim={c.data_fim}
              regras={c.regras}
            />
          ))}
        </SeccaoBlock>
      )}
    </div>
  );
};

export default CampanhasEmVigorTab;
